/* eslint-disable */
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { HandlingUnitGroup, InventoryCountOrder, Product, RootState } from '../../store/config/types';
import Card from '../../components/Card/Card';
import CardBody from '../../components/Card/CardBody';
import CardHeader from '../../components/Card/CardHeader';
import { setToast } from '../../store/actions/toast.actions';
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { AutoComplete } from '../../components/AutoComplete/AutoComplete';
import WarehouseWorkService from '../../services/warehouseWork.service';
import DatePicker from '../../components/DatePicker/DatePicker';
import { Warehouse } from '../../store/config/types/warehouses.types';
import { getUserDisplayName } from '../../utils/string.helpers';
import { Table } from '@material-ui/core';
import ToggleSwitch from '../../components/Switch/Switch';
import moment from 'moment-timezone';
import { Delete } from '@material-ui/icons';
import InventoryCountItemList from './InventoryCountItemList';

interface InventoryCountParams {
  id?: string;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth?.account,
  warehouses: state.warehouses,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, any>) => ({
  setToast: (message: string, messageType: string) => dispatch(setToast(message, messageType)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface InventoryCountItemStruct {
  product?: Product | undefined;
  lotId?: string | undefined;
  identificationData?: any;
  comment?: string | undefined;
}

const InventoryCountForm = ({ setToast, warehouses, user }: PropsFromRedux) => {
  const { id } = useParams<InventoryCountParams>();
  //   const editMode: boolean = Boolean(id);
  const history = useHistory();
  const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [wmsUsers, setWmsUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>(''); // State for selected user
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>();
  const [items, setItems] = useState<InventoryCountItemStruct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [erroMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (user) {
      WarehouseWorkService.getWmsUsers()
        .then((users) => {
          if (users?.length) {
            setWmsUsers(users);
          }
        })
        .catch((err) => {
          if (err.response?.data?.message) {
            setErrorMessage(err.response.data.message);
          } else {
            setToast(err.message, 'danger');
          }
        });
    }
  }, [user]);

  const warehouseSearch = async (query: string) => {
    if (warehouses?.warehouses?.length) {
      return warehouses.warehouses.filter((w) =>
        `${w.externalId} ${w.name}`.toLowerCase().includes(query.toLowerCase()),
      );
    }
    return [];
  };

  const handleGoBack = () => {
    if (loading) return;
    history.goBack();
  };

  const submitForm = async () => {
    if (loading) return;
    const errors: string[] = [];
    if (!date) errors.push('Date not set');
    if (!selectedWarehouse) errors.push('Warehouse not selected');
    if (!selectedUser) errors.push('User not assigned');
    if (items.length == 0) errors.push('No items added');
    if (errors.length) {
      setToast(errors.join(', '), 'danger');
      return;
    }

    const order: Partial<InventoryCountOrder> = {
      date: date,
      warehouseId: selectedWarehouse?.warehouseId,
      warehouse: selectedWarehouse,
      warehouseUserId: selectedUser ? Number(selectedUser) : undefined,
      orderInfo: {
        items: items.map((i) => {
          return {
            productId: i.product?.productId,
            product: i.product,
            handlingUnitGroups: [
              {
                displayName: i.lotId ?? 'All',
                identificationData: i.identificationData,
                type: i.lotId ? 'LOT' : 'UNTYPED',
                unit: i.product?.unitOfMeasure,
                handlingUnits: [],
                additionalData: {},
              },
            ],
            comment: i.comment,
          };
        }),
        actionHistory: [],
      },
    };
    setLoading(true);
    setErrorMessage(undefined);
    WarehouseWorkService.createInventoryCountOrder(order)
      .then(() => {
        setToast('Inventory Count Order created successfully', 'success');
        history.goBack();
      })
      .catch((err: any) => {
        console.error(err.response);

        if (err.response?.data?.data?.message) {
          setErrorMessage(err.response.data.data.message);
        } else {
          setToast(err.message, 'danger');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function handleAddItem(info: InventoryCountItemStruct): void {
    const newItems = [...items];
    const itemIdx = items.findIndex((i) => i.product?.productId == info.product?.productId && info.lotId == i.lotId);
    if (itemIdx < 0) {
      newItems.push(info);
    } else {
      newItems[itemIdx] = info;
    }
    setItems(newItems);
  }
  function handleRemoveItem(index: number) {
    setItems(items.filter((_, i) => i !== index));
  }

  function handleAllItemAddClick() {
    if (!selectedWarehouse) return;
    setLoading(true);
    WarehouseWorkService.getAllAvailableProductsOfWarehouse(selectedWarehouse.warehouseId)
      .then((products) => {
        setItems(products.map((product) => ({ product })));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <div className="courier-form">
      <Card profile>
        <CardHeader color="primary" className="card-header">
          <h4>New Inventory Count</h4>
        </CardHeader>
        <CardBody profile>
          <Grid container spacing={2} p={2}>
            <Grid item sm={12} lg={4}>
              <DatePicker
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                }}
                label="Date"
                name="date"
                margin="none"
                disabled={loading}
              />
            </Grid>
            <Grid item sm={12} lg={4}>
              <AutoComplete<Warehouse>
                label="Warehouse"
                style={{ width: '100%' }}
                fetchFn={warehouseSearch}
                getItemValue={(warehouse) => `${warehouse.externalId} - ${warehouse.name}`}
                onSelect={(w) => {
                  setSelectedWarehouse(w);
                }}
                renderItem={(warehouse) => `${warehouse.externalId} - ${warehouse.name}`}
                minLength={1}
                disabled={loading}
              />
            </Grid>

            <Grid item sm={12} lg={4}>
              <FormControl fullWidth>
                <InputLabel id="wms-user-select-label">Assign To</InputLabel>
                <Select
                  labelId="wms-user-select-label"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  label="Warehouse User"
                  disabled={loading}
                >
                  {wmsUsers.map((user) => (
                    <MenuItem key={user.warehouseUserId} value={user.warehouseUserId}>
                      {getUserDisplayName(user)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} p={2} mt={2}>
            <Grid container justifyContent="space-between">
              <Typography variant="button">ITEMS</Typography>
            </Grid>
            <InventoryCountItemList items={items as any} handleRemoveItem={loading ? undefined : handleRemoveItem} />
            <Grid container mt={2} gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setItemDialogOpen(true)}
                disabled={!Boolean(selectedWarehouse) || loading}
              >
                New Item
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAllItemAddClick}
                disabled={!Boolean(selectedWarehouse) || loading}
              >
                Add All Warehouse Items
              </Button>
            </Grid>
          </Grid>
          {erroMessage && (
            <Grid container gap={2} p={2} mt={2}>
              <Alert severity="error">{erroMessage}</Alert>
            </Grid>
          )}
          <Grid container gap={2} p={2} mt={2} justifyContent="center">
            <Button color="primary" variant="contained" onClick={submitForm} disabled={loading}>
              {loading ? 'Creating...' : 'Create Order'}
            </Button>
            <Button variant="outlined" onClick={handleGoBack} disabled={loading}>
              Cancel
            </Button>
          </Grid>
        </CardBody>
        <ProductLotDialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          warehouse={selectedWarehouse}
          onSubmit={handleAddItem}
        />
      </Card>
    </div>
  );
};

interface ProductLotDialogProps {
  open: boolean;
  warehouse?: Warehouse;
  onSubmit?: (info: { product?: Product; lotId?: string; comments?: string }) => void;
  onClose?: () => void;
}
function ProductLotDialog({ open, onSubmit, onClose, warehouse }: ProductLotDialogProps) {
  const [specificLot, setSpecificLot] = useState(false);
  const [hasLot, setHasLot] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [comments, setComments] = useState<string>('');
  const [lot, setLot] = useState<any>({});
  useEffect(() => {
    if (!open) {
      setSpecificLot(false);
      setSelectedProduct(undefined);
      setHasLot(false);
      setComments('');
      setLot({});
    }
  }, [open]);
  useEffect(() => {
    const lotNumbered = Boolean(selectedProduct?.type?.toLowerCase().includes('lot'));
    setHasLot(lotNumbered);
    if (!lotNumbered) {
      setSpecificLot(false);
      setLot({});
    }
  }, [selectedProduct]);

  const okToSubmit = () => {
    if (!selectedProduct) return false;
    return true;
  };

  const lotSearchFn = async (query: string) => {
    const ids: any[] = [];
    if (selectedProduct && hasLot && specificLot) {
      try {
        const lotIds = await WarehouseWorkService.searchProductLot(
          query,
          warehouse?.warehouseId,
          selectedProduct?.productId,
        );
        // lotIds.forEach((lot) => {
        //   ids.push(lot.lotId);
        // });
        return lotIds;
      } catch {}
    }
    return [];
  };

  function handleSubmit() {
    if (!okToSubmit()) return false;
    const info: InventoryCountItemStruct = {
      product: selectedProduct,
      lotId: hasLot && specificLot ? lot.lotId : undefined,
      identificationData: {
        externalId: lot.externalId,
        externalSource: lot.externalSource,
        inventoryLotId: lot.inventoryLotId,
      },
      comment: comments,
    };
    if (onSubmit) {
      onSubmit(info);
      onClose && onClose();
    }
  }
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Item</DialogTitle>
      <DialogContent style={{ minHeight: 300 }}>
        <Grid container spacing={2} p={2}>
          <Grid item sm={12}>
            <AutoComplete<Product>
              label="Product"
              style={{ width: '100%' }}
              fetchFn={WarehouseWorkService.searchProduct}
              getItemValue={(product) => `${product.externalId} - ${product.name}`}
              onSelect={(product: Product) => {
                setSelectedProduct(product);
              }}
              renderItem={(product) => `${product.externalId} - ${product.name}`}
            />
          </Grid>
          <Grid item sm={12}>
            <ToggleSwitch
              checked={specificLot}
              disabled={!hasLot}
              label={'Specific Lot'}
              name="speceficLot"
              setFieldValue={(name, value) => {
                setSpecificLot(value);
              }}
            />
          </Grid>
          <Grid item sm={12}>
            <AutoComplete
              disabled={!specificLot || !hasLot}
              label="Lot"
              style={{ width: '100%' }}
              fetchFn={lotSearchFn}
              getItemValue={(lot) => lot.lotId}
              onSelect={(l) => {
                setLot(l);
              }}
              renderItem={(lot) => lot.lotId}
              minLength={1}
            />
          </Grid>
          <Grid item sm={12}>
            <FormControl fullWidth>
              <TextField onChange={(e) => setComments(e.target.value)} label="Comments" value={comments} />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default connector(InventoryCountForm);
